import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  ImageList,
  ImageListItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { uploadImagesAndSaveRecords } from '../../ImageSubmission/uploadForm';
import { deleteImagesFunction } from '../../../firebase/functions';
import '../../StaffGallery/StaffGallery.css';
import { allMonths } from '../CalendarEditor';
import { getNewYorkTimeZoneDate } from '../../../utils/dayjsUs';
const CalendarTemplate = ({
  facilityId,
  claims,
  calendarTemplateImages,
  userUid,
  setCalendarTemplateImages,
  setIsLoading,
  getCalendarImages,
  facilityData,
  setFacilityData,
  month,
}) => {
  const [openUpload, setOpenUpload] = useState(false);
  const [openSelect, setOpenSelect] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    getNewYorkTimeZoneDate().format('MMMM'),
  );
  const [selectedImage, setSelectedImage] = useState(null);
  const [scope, setScope] = useState('global');

  const openUploadDialog = () => setOpenUpload(true);
  const closeUploadDialog = () => {
    setOpenUpload(false);
    setSelectedImages([]);
    setImagePreviewUrls([]);
    setScope('global');
  };

  const closeSelectDialog = () => {
    setOpenSelect(false);
    if (
      calendarTemplateImages.length &&
      facilityData?.calendarTemplateImageId
    ) {
      setSelectedImage(
        calendarTemplateImages.find(
          (img) => img.id === facilityData.calendarTemplateImageId,
        ),
      );
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(files);
    setImagePreviewUrls(files.map((f) => URL.createObjectURL(f)));
  };

  const handleUploadSave = async () => {
    if (!selectedImages.length) return;
    closeUploadDialog();
    setIsLoading(true);
    try {
      await uploadImagesAndSaveRecords(
        { type: 'calendarTemplate', month: selectedMonth, scope },
        selectedImages,
        facilityId,
        userUid,
      );
      await getCalendarImages();
    } finally {
      setIsLoading(false);
    }
  };

  const handleChooseImage = (id) =>
    setSelectedImage((prev) =>
      prev?.id === id
        ? null
        : calendarTemplateImages.find((img) => img.id === id),
    );

  const disabledCheckbox = (id) =>
    !selectedImage || id === selectedImage.id ? false : true;

  const handleSaveSelectedImage = async (deletedImage = false) => {
    setIsLoading(true);

    const imageUrl =
      selectedImage && !deletedImage ? selectedImage.imageUrl : null;
    const imageId = selectedImage && !deletedImage ? selectedImage.id : null;

    try {
      const q = query(
        collection(db, 'facilities'),
        where('facilityId', '==', facilityId),
        where('month', '==', month),
      );
      const snap = await getDocs(q);

      if (snap.empty) {
        await addDoc(collection(db, 'facilities'), {
          facilityId,
          month,
          calendarTemplateImage: imageUrl,
          calendarTemplateImageId: imageId,
        });
      } else {
        await Promise.all(
          snap.docs.map((d) =>
            updateDoc(d.ref, {
              calendarTemplateImage: imageUrl,
              calendarTemplateImageId: imageId,
            }),
          ),
        );
      }

      setFacilityData({
        ...facilityData,
        calendarTemplateImage: imageUrl,
        calendarTemplateImageId: imageId,
      });
    } catch (err) {
      console.error('Error updating facility:', err);
    } finally {
      setIsLoading(false);
      setOpenSelect(false);
      setSelectedImage(null);
    }
  };

  const deleteSelectedImage = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this image? This action is irreversible!',
      )
    )
      return;

    setOpenSelect(false);
    setIsLoading(true);

    try {
      await deleteImagesFunction({
        imageUrls: [selectedImage.imageUrl],
        deleteFromFirestore: true,
      });

      setCalendarTemplateImages((imgs) =>
        imgs.filter((img) => img.id !== selectedImage.id),
      );

      await handleSaveSelectedImage(true);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setSelectedImage(
      calendarTemplateImages.find(
        (img) => img.id === facilityData?.calendarTemplateImageId,
      ) || null,
    );
  }, [facilityData, facilityId, calendarTemplateImages]);

  return (
    <>
      <Box
        sx={{ width: '100%', pt: 2, display: 'flex', flexDirection: 'column' }}
      >
        <Typography variant="h5" fontWeight="bold">
          Template:
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            py: 1.5,
            borderBottom: '4px solid black',
          }}
        >
          <Box
            sx={{
              width: '30%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 20,
              }}
              onClick={() => setOpenSelect(true)}
            >
              Modify
            </Button>
            {claims?.superadmin && (
              <Button
                variant="contained"
                onClick={openUploadDialog}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 20,
                }}
              >
                Upload
              </Button>
            )}
          </Box>
          <Box sx={{ width: '70%' }}>
            {facilityData?.calendarTemplateImage && (
              <img
                src={facilityData.calendarTemplateImage}
                alt="Selected calendar template"
                style={{ width: '100%', maxHeight: 300, objectFit: 'contain' }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={openSelect}
        onClose={closeSelectDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Image Template</DialogTitle>
        <DialogContent>
          {selectedImage && claims?.superadmin && (
            <Box sx={{ mb: 2 }}>
              <IconButton
                onClick={deleteSelectedImage}
                sx={{
                  backgroundColor: '#004B8D',
                  color: '#fff',
                  width: 150,
                  borderRadius: 2,
                  fontSize: 25,
                  '&:hover': { backgroundColor: '#808080' },
                }}
              >
                <DeleteIcon />
                Delete
              </IconButton>
            </Box>
          )}

          {(() => {
            const globalTemplates = calendarTemplateImages.filter((img) => img.scope !== 'facility');
            const facilityTemplates = calendarTemplateImages.filter((img) => img.scope === 'facility');
            return (
              <>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>
                  Global Templates
                </Typography>
                {globalTemplates.length ? (
                  <ImageList cols={3} variant="masonry">
                    {globalTemplates.map((img) => (
                      <ImageListItem key={img.id}>
                        <img
                          src={img.imageUrl}
                          alt="template"
                          loading="lazy"
                          style={{
                            width: '100%',
                            transition: 'opacity .4s',
                            opacity:
                              selectedImage?.id === img.id
                                ? 0.8
                                : disabledCheckbox(img.id)
                                  ? 0.2
                                  : 1,
                          }}
                        />
                        <Checkbox
                          checked={selectedImage?.id === img.id}
                          onChange={() => handleChooseImage(img.id)}
                          disabled={disabledCheckbox(img.id)}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                ) : (
                  <Typography sx={{ mt: 1, mb: 2, color: 'gray', fontStyle: 'italic' }}>
                    No Global Templates For This Month
                  </Typography>
                )}

                <Box sx={{ my: 2, borderTop: '1px solid #ccc', pt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Facility-Specific Templates
                  </Typography>
                  {facilityTemplates.length ? (
                    <ImageList cols={3} variant="masonry">
                      {facilityTemplates.map((img) => (
                        <ImageListItem key={img.id}>
                          <img
                            src={img.imageUrl}
                            alt="template"
                            loading="lazy"
                            style={{
                              width: '100%',
                              transition: 'opacity .4s',
                              opacity:
                                selectedImage?.id === img.id
                                  ? 0.8
                                  : disabledCheckbox(img.id)
                                    ? 0.2
                                    : 1,
                            }}
                          />
                          <Checkbox
                            checked={selectedImage?.id === img.id}
                            onChange={() => handleChooseImage(img.id)}
                            disabled={disabledCheckbox(img.id)}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  ) : (
                    <Typography sx={{ mt: 1, mb: 2, color: 'gray', fontStyle: 'italic' }}>
                      No Facility-Specific Templates Uploaded for This Month
                    </Typography>
                  )}
                </Box>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
          <Button onClick={closeSelectDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleSaveSelectedImage(false)}
            disabled={!selectedImage && !facilityData?.calendarTemplateImageId}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openUpload}
        onClose={closeUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Image Templates</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {imagePreviewUrls.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  justifyContent: 'center',
                }}
              >
                {imagePreviewUrls.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`preview-${i}`}
                    style={{
                      maxWidth: 100,
                      maxHeight: 100,
                      borderRadius: 8,
                      objectFit: 'cover',
                    }}
                  />
                ))}
              </Box>
            )}

            <Button variant="contained" component="label">
              Select Images
              <input
                type="file"
                accept="image/*"
                hidden
                multiple
                onChange={handleImageSelect}
              />
            </Button>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="month-label" shrink={true}>
                Select Month
              </InputLabel>
              <Select
                labelId="month-label"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                sx={{ height: 46 }}
                label="Select Month"
                displayEmpty
              >
                {allMonths.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="scope-label" shrink={true}>
                Availability Scope
              </InputLabel>
              <Select
                labelId="scope-label"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                sx={{ height: 46 }}
                label="Availability Scope"
                displayEmpty
              >
                <MenuItem value="global">Global (All Facilities)</MenuItem>
                <MenuItem value="facility">Facility-Specific (Current Facility Only)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', mb: 1 }}>
          <Button onClick={closeUploadDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadSave}
            disabled={!selectedImages.length}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

CalendarTemplate.propTypes = {
  facilityId: PropTypes.string,
  claims: PropTypes.object,
  calendarTemplateImages: PropTypes.array,
  userUid: PropTypes.string,
  setCalendarTemplateImages: PropTypes.func,
  setIsLoading: PropTypes.func,
  getCalendarImages: PropTypes.func,
  facilityData: PropTypes.object,
  setFacilityData: PropTypes.func,
  month: PropTypes.string,
};
export default CalendarTemplate;
